import type { Booking } from '@zuruflex/types';
import { api } from './api.js';

/**
 * A hire as the portal reads it: the booking, plus the car the API joins
 * onto every row.
 */
export interface HireRow extends Booking {
  vehicle?: { id: string; make: string; model: string; plateNumber: string } | null;
}

/** The API caps a page at a hundred; asking for more is a validation error. */
const PER_PAGE = 100;

/**
 * How far back the dashboard is willing to read.
 *
 * A year of history is what the charts draw, and five hundred hires is
 * comfortably more than a year for the fleets this portal serves. The cap
 * exists so that an unusually busy partner costs five requests rather than
 * fifty — the page would rather draw a slightly short series than spend a
 * minute being exact about a bar nobody is looking at.
 */
const MAX_PAGES = 5;

/**
 * Every hire this partner has, newest first.
 *
 * The overview needs the whole book, not a page of it: a chart of the last
 * twelve months and a total earned cannot be computed from the hundred
 * most recent rows. The API orders by creation date descending and scopes
 * to the caller's own partner, so page one is already the right hundred
 * and the rest are fetched together rather than in a chain.
 */
export async function fetchHires(): Promise<HireRow[]> {
  const first = (await api.listBookings({ perPage: PER_PAGE })) as {
    items: HireRow[];
    totalPages: number;
  };

  const lastPage = Math.min(first.totalPages, MAX_PAGES);
  if (lastPage <= 1) return first.items;

  const rest = await Promise.all(
    Array.from({ length: lastPage - 1 }, (_unused, index) =>
      api.listBookings({ page: index + 2, perPage: PER_PAGE }),
    ),
  );

  return [...first.items, ...rest.flatMap((page) => page.items as HireRow[])];
}
